import React from "react";
import { Box, Switch, Typography } from "@mui/joy";
import { Alert, Button } from "@mui/material";
import { Formik, FormikHelpers } from "formik";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { LoadingScreen } from "@dreebit-registry/vsm-microservice-components";
import { resolveErrorMessage } from "../../utils/resolveErrorMessage";
import { UpdateNotificationSettings } from "../../graphql/mutations/updateNotificationSettings";
import { useNotificationsContext } from "../NotificationsProvider";
import ScheduleDayRow, { DayValues } from "./ScheduleDayRow";
import { NotificationSettingsScheduleDay } from "../../codegen/graphql";
import useEmailVerificationStatus from "../../hooks/useEmailVerificationStatus";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"] as const;
type DayKey = typeof DAYS[number];

interface FormValues {
    general: { push: boolean; inApp: boolean; email: boolean };
    schedule: { isEnabled: boolean } & Record<DayKey, DayValues>;
}

const dayInit = (d?: NotificationSettingsScheduleDay | null): DayValues => ({
    isEnabled: !!d?.isEnabled,
    from: d?.from ?? "08:00",
    to: d?.to ?? "18:00",
});

const NotificationsSettings = () => {
    const { t } = useTranslation();
    const { settings, loading, initializing, refetchSettings } = useNotificationsContext();
    const [updateMutation] = useMutation(UpdateNotificationSettings);
    // Unverified email blocks every channel on the backend, not just mail.
    const { unverified: emailUnverified } = useEmailVerificationStatus();

    if (loading || initializing) return <LoadingScreen />;
    if (!settings) {
        return (
            <Box sx={{ padding: "12px 36px" }}>
                <Typography level="body-md" color="danger">
                    {t("Failed to load notification settings.")}
                </Typography>
            </Box>
        );
    }

    const initialValues: FormValues = {
        general: {
            push: !!settings?.general?.push,
            inApp: !!settings?.general?.inApp,
            email: !!settings?.general?.email,
        },
        schedule: {
            isEnabled: !!settings?.schedule?.isEnabled,
            monday: dayInit(settings?.schedule?.monday),
            tuesday: dayInit(settings?.schedule?.tuesday),
            wednesday: dayInit(settings?.schedule?.wednesday),
            thursday: dayInit(settings?.schedule?.thursday),
            friday: dayInit(settings?.schedule?.friday),
            saturday: dayInit(settings?.schedule?.saturday),
            sunday: dayInit(settings?.schedule?.sunday),
        },
    };

    const onSubmit = (values: FormValues, helpers: FormikHelpers<FormValues>) => {
        const scheduleInput: Record<string, unknown> = { isEnabled: values.schedule.isEnabled };
        DAYS.forEach((day) => {
            scheduleInput[day] = {
                isEnabled: values.schedule[day].isEnabled,
                from: values.schedule[day].from,
                to: values.schedule[day].to,
            };
        });

        updateMutation({
            variables: {
                input: {
                    general: values.general,
                    schedule: scheduleInput,
                },
            },
        })
            .then(async () => {
                await refetchSettings();
                toast.success(t("Update successful."));
            })
            .catch((err) => {
                toast.error(resolveErrorMessage(err, t));
            })
            .finally(() => helpers.setSubmitting(false));
    };

    const dayLabels: Record<DayKey, string> = {
        monday: t("Monday"),
        tuesday: t("Tuesday"),
        wednesday: t("Wednesday"),
        thursday: t("Thursday"),
        friday: t("Friday"),
        saturday: t("Saturday"),
        sunday: t("Sunday"),
    };

    return (
        <Box sx={{ padding: "12px 36px", width: "100%", maxWidth: "720px", overflowY: "auto", minHeight: 0 }}>
            {emailUnverified && (
                <Alert severity="warning" sx={{ mb: "20px" }} data-testid="email-unverified-hint">
                    {t("Your email address is not verified yet. No notifications will be delivered until it is verified.")}
                </Alert>
            )}
            <Formik initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
                {({ values, setFieldValue, isSubmitting, handleSubmit, dirty }) => (
                    <form onSubmit={handleSubmit}>
                        <Box sx={{ marginBottom: "12px", borderBottom: "1px solid #ccc" }}>
                            <Typography level="titleLarge" sx={{ paddingBottom: "8px" }}>
                                {t("General")}
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5, mb: 4 }}>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Switch
                                    checked={values.general.push}
                                    onChange={(e) => setFieldValue("general.push", e.target.checked)}
                                />
                                <Typography level="body-md">{t("Push notifications")}</Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Switch
                                    checked={values.general.inApp}
                                    onChange={(e) => setFieldValue("general.inApp", e.target.checked)}
                                />
                                <Typography level="body-md">{t("In-app notifications")}</Typography>
                            </Box>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Switch
                                    checked={values.general.email}
                                    onChange={(e) => setFieldValue("general.email", e.target.checked)}
                                />
                                <Typography level="body-md">{t("Email notifications")}</Typography>
                            </Box>
                        </Box>

                        <Box sx={{ marginBottom: "12px", borderBottom: "1px solid #ccc" }}>
                            <Typography level="titleLarge" sx={{ paddingBottom: "8px" }}>
                                {t("Schedule")}
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}>
                            <Switch
                                checked={values.schedule.isEnabled}
                                onChange={(e) => setFieldValue("schedule.isEnabled", e.target.checked)}
                            />
                            <Typography level="body-md">
                                {t("Restrict notifications to a weekly schedule")}
                            </Typography>
                        </Box>
                        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, mb: 4 }}>
                            {DAYS.map((day) => (
                                <ScheduleDayRow
                                    key={day}
                                    label={dayLabels[day]}
                                    value={values.schedule[day]}
                                    disabled={!values.schedule.isEnabled}
                                    onChange={(next) => setFieldValue(`schedule.${day}`, next)}
                                />
                            ))}
                        </Box>

                        <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                            <Button
                                type="submit"
                                variant="contained"
                                disabled={isSubmitting || !dirty}
                            >
                                {t("Save")}
                            </Button>
                        </Box>
                    </form>
                )}
            </Formik>
        </Box>
    );
};

export default NotificationsSettings;
